import React, { useEffect, useState } from 'react';
import { User } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';

const getMediaUrl = (src) => resolveMediaUrl(src) || '';

export const KMA_OPTIONS = ['kiss', 'marry', 'avoid'];
export const KMA_DEFAULT_COLORS = { kiss: '#e11d48', marry: '#2A4D25', avoid: '#6b7280' };
export const KMA_DEFAULT_LABELS = { kiss: 'Kiss', marry: 'Marry', avoid: 'Avoid' };

// Kiss / Marry / Avoid — three characters, three options, and every option can
// be given to only one character. Picks are stored as { characterId: option };
// an option already given to someone else is disabled until it's freed up.
const KissMarryAvoidElement = ({
  element,
  isEditor = false,
  canInteract = true,
  onPicksChange,
  syncedPicks = null,
  slideScale = 1,
}) => {
  const { t } = useTranslation();
  const { content } = element;
  const {
    title = '',
    characters = [],
    labels = {},
    kissColor = KMA_DEFAULT_COLORS.kiss,
    marryColor = KMA_DEFAULT_COLORS.marry,
    avoidColor = KMA_DEFAULT_COLORS.avoid,
    cardBgColor = '#ffffff',
    textColor = '#1f2937',
    fontFamily = 'Evolventa',
    fontSize = 18,
  } = content || {};
  const colors = { kiss: kissColor, marry: marryColor, avoid: avoidColor };
  const labelFor = (opt) => (labels[opt] && labels[opt].trim()) || KMA_DEFAULT_LABELS[opt];

  const [localPicks, setLocalPicks] = useState(() => (syncedPicks && typeof syncedPicks === 'object' ? syncedPicks : {}));
  useEffect(() => {
    if (syncedPicks && typeof syncedPicks === 'object') setLocalPicks(syncedPicks);
  }, [syncedPicks]);

  const interactive = !isEditor && canInteract;
  const characterIds = new Set(characters.map((c) => c.id));
  // Ignore picks for characters the teacher has since removed.
  const picks = Object.fromEntries(Object.entries(localPicks).filter(([id]) => characterIds.has(id)));
  const ownerOf = (opt) => Object.keys(picks).find((id) => picks[id] === opt) || null;

  const choose = (characterId, opt) => {
    if (!interactive) return;
    const owner = ownerOf(opt);
    if (owner && owner !== characterId) return;
    const next = { ...picks };
    if (owner === characterId) delete next[characterId];
    else next[characterId] = opt;
    setLocalPicks(next);
    if (onPicksChange) onPicksChange(next);
  };

  const s = typeof slideScale === 'number' && slideScale > 0 ? slideScale : 1;
  const fs = Math.max(8, fontSize * s);
  const pad = Math.max(6, Math.round(14 * s));
  const gap = Math.max(4, Math.round(12 * s));

  return (
    <div
      className="w-full h-full flex flex-col overflow-hidden"
      style={{ padding: pad, gap, fontFamily, color: textColor }}
    >
      {title && (
        <div className="text-center font-bold leading-tight shrink-0" style={{ fontSize: fs * 1.3 }}>
          {title}
        </div>
      )}
      <div
        className="flex-1 min-h-0"
        style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, characters.length)}, minmax(0, 1fr))`, gap }}
      >
        {characters.map((c) => {
          const picked = picks[c.id];
          return (
            <div
              key={c.id}
              className="flex flex-col min-h-0 rounded-2xl overflow-hidden transition"
              style={{
                backgroundColor: cardBgColor,
                border: `2px solid ${picked ? colors[picked] : 'rgba(0,0,0,0.08)'}`,
                padding: Math.round(gap * 0.8),
                gap: Math.round(gap * 0.6),
              }}
            >
              <div className="flex-1 min-h-0 flex items-center justify-center rounded-xl overflow-hidden" style={{ backgroundColor: c.imageUrl ? 'transparent' : 'rgba(0,0,0,0.04)' }}>
                {c.imageUrl ? (
                  <img
                    src={getMediaUrl(c.imageUrl)}
                    alt={c.name || ''}
                    draggable={false}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <User size={Math.round(fs * 3)} className="text-gray-300" />
                )}
              </div>
              {(c.name || !c.imageUrl) && (
                <div className="text-center font-semibold leading-tight break-words shrink-0" style={{ fontSize: fs }}>
                  {c.name || '?'}
                </div>
              )}
              <div className="flex flex-col shrink-0" style={{ gap: Math.round(gap * 0.4) }}>
                {KMA_OPTIONS.map((opt) => {
                  const isMine = picked === opt;
                  const takenByOther = !isMine && !!ownerOf(opt);
                  const color = colors[opt];
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => choose(c.id, opt)}
                      disabled={!interactive || takenByOther}
                      className="flex items-center justify-center rounded-full font-medium transition"
                      style={{
                        gap: Math.round(gap * 0.4),
                        padding: `${Math.round(5 * s)}px ${Math.round(10 * s)}px`,
                        fontSize: fs * 0.85,
                        fontFamily,
                        backgroundColor: isMine ? color : 'transparent',
                        color: isMine ? '#ffffff' : color,
                        border: `2px solid ${color}`,
                        opacity: takenByOther ? 0.3 : 1,
                        cursor: interactive && !takenByOther ? 'pointer' : 'default',
                      }}
                      title={takenByOther
                        ? t('games.kissMarryAvoid.taken', { defaultValue: 'Вже обрано для іншого персонажа' })
                        : undefined}
                    >
                      <span className="leading-none">{labelFor(opt)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KissMarryAvoidElement;
